import React, { useState } from 'react';
import { Layout, Menu, Button, Typography, Space, Drawer } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  CalendarOutlined,
  LogoutOutlined,
  ExperimentOutlined,
  TeamOutlined,
  SettingOutlined,
  BookOutlined,
  InboxOutlined,
  MenuOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

import PatientChartDrawer from './PatientChartDrawer';
import ChatIcon from './ChatIcon';
import LanguageToggle from './LanguageToggle';
import UserMenu from './UserMenu';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const MainLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isChatDropdownVisible, setIsChatDropdownVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Responsive: detect mobile
  const isMobile = window.innerWidth < 768;

  // Don't show layout on login page
  if (location.pathname === '/login') {
    return children;
  }

  const handleChatDropdownVisibleChange = (visible) => {
    setIsChatDropdownVisible(visible);
  };

  const baseMenuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: t('navigation.dashboard'),
      onClick: () => {
        navigate('/dashboard');
        setDrawerVisible(false);
      }
    },
    {
      key: '/patients',
      icon: <UserOutlined />,
      label: t('navigation.patients'),
      onClick: () => {
        navigate('/patients');
        setDrawerVisible(false);
      }
    },
    {
      key: '/appointments',
      icon: <CalendarOutlined />,
      label: t('navigation.appointments'),
      onClick: () => {
        navigate('/appointments');
        setDrawerVisible(false);
      }
    },
    {
      key: '/work-basket',
      icon: <InboxOutlined />,
      label: t('navigation.workBasket'),
      onClick: () => {
        navigate('/work-basket');
        setDrawerVisible(false);
      }
    },
    {
      key: '/lab-management',
      icon: <ExperimentOutlined />,
      label: t('navigation.laboratory'),
      onClick: () => {
        navigate('/lab-management');
        setDrawerVisible(false);
      }
    }
  ];

  const adminMenuItems = user?.role === 'Admin' ? [
    {
      key: '/user-management',
      icon: <TeamOutlined />,
      label: t('navigation.userManagement'),
      onClick: () => {
        navigate('/user-management');
        setDrawerVisible(false);
      }
    },
    {
      key: '/clinical-settings',
      icon: <SettingOutlined />,
      label: t('navigation.clinicalSettings'),
      onClick: () => {
        navigate('/clinical-settings');
        setDrawerVisible(false);
      }
    }
  ] : [];

  const menuItems = [...baseMenuItems, ...adminMenuItems];
  const selectedKey = location.pathname;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Main Header with Horizontal Navigation */}
      <Header
        style={{
          background: '#fff',
          padding: isMobile ? '0 12px' : '0 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'fixed',
          width: '100%',
          top: 0,
          zIndex: 1000,
          height: isMobile ? '56px' : '64px'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          minWidth: 0
        }}>
          <img
            src="/icons/care360-logo.png"
            alt="Care360"
            style={{
              height: isMobile ? '32px' : '40px',
              marginRight: isMobile ? 16 : 32,
              objectFit: 'contain'
            }}
          />

          {/* Mobile: show menu button */}
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined style={{ fontSize: 24 }} />}
              onClick={() => setDrawerVisible(true)}
              style={{ marginLeft: 8 }}
            />
          ) : (
            <Menu
              mode="horizontal"
              selectedKeys={[selectedKey]}
              style={{
                border: 'none',
                background: 'transparent',
                flex: 1,
                minWidth: 0
              }}
              items={menuItems}
            />
          )}
        </div>

        <Space size={isMobile ? 8 : "middle"}>
          <LanguageToggle
            type="default"
            style={{
              color: 'rgba(0, 0, 0, 0.85)',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              padding: isMobile ? '0 4px' : undefined
            }}
          />
          <UserMenu />
        </Space>
      </Header>

      {/* Drawer for mobile menu */}
      <Drawer
        title={t('navigation.menu')}
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          style={{ border: 'none' }}
        />
      </Drawer>

      {/* Main Content Area */}
      <Content style={{
        marginTop: isMobile ? '56px' : '64px',
        padding: isMobile ? '12px' : '24px',
        background: '#f0f2f5',
        minHeight: `calc(100vh - ${isMobile ? 56 : 64}px)`
      }}>
        <div style={{
          background: '#fff',
          padding: isMobile ? '12px' : '24px',
          borderRadius: '8px',
          minHeight: `calc(100vh - ${isMobile ? 68 : 112}px)`
        }}>
          {children}
        </div>
      </Content>

      {/* Patient Chart Drawer */}
      <PatientChartDrawer isChatDropdownVisible={isChatDropdownVisible} />

      {/* Floating Chat Icon */}
      <ChatIcon
        userId={user?._id}
        isChatDropdownVisible={isChatDropdownVisible}
        onChatDropdownVisibleChange={handleChatDropdownVisibleChange}
      />
    </Layout>
  );
};

export default MainLayout;